import { Section, H2, DataField } from "./common-components";
import {
  capitalizeFirstLetter,
  formatDateToDD_MMM_YYYY,
  formatTimeFromISO,
} from "../../../helpers/functions";
import { GetDropDownValue } from "./data";
import {
  reasonStartOptions,
  followUpTopicOptions,
} from "../../patient/clinical-services/service-steps/education/data";
import useFollowUpSuppression from "../../../hooks/useFollowUpSuppression";

const isAllergicRhinitis = (name) => {
  const n = (name ?? "").toString().toLowerCase();
  return n.includes("allergic") || n === "allergic rhinitis";
};

const getTopicLabels = (topicValues) => {
  if (!Array.isArray(topicValues) || topicValues.length === 0) return [];
  return topicValues
    .filter((v) => v && String(v).toLowerCase() !== "nofollowup")
    .map((v) => GetDropDownValue(followUpTopicOptions, v) ?? v);
};

const getFollowUpValue = (education) => {
  if (!education?.followUpDate) return "No Follow Up";
  const d = formatDateToDD_MMM_YYYY(education.followUpDate);
  const t = formatTimeFromISO(education.followUpDate);
  return `${d}${t ? ` @ ${t}` : ""}`;
};

const getTopicsValue = (education, clinicalServiceName) => {
  if (!education) return "";
  if (isAllergicRhinitis(clinicalServiceName)) {
    return GetDropDownValue(reasonStartOptions, education.followUp) ?? "";
  }
  const labels = getTopicLabels(education.followUpType ?? []);
  return labels.join(", ");
};

const formatClinicalServiceName = (name) => {
  const raw = (name ?? "").toString().trim();
  if (!raw) return "Medication Review";
  // Insert spaces for camelCase, replace underscores/hyphens, then normalize spacing
  const spaced = raw.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_\-]+/g, " ");
  return spaced
    .split(/\s+/)
    .map((w) => capitalizeFirstLetter(String(w || "").toLowerCase()))
    .join(" ");
};

const getFollowUpRowLabel = (clinicalServiceName) => {
  const name = formatClinicalServiceName(clinicalServiceName);
  return `${name} Follow Up`;
};

const getOtherRowLabel = (clinicalServiceName) =>
  isAllergicRhinitis(clinicalServiceName)
    ? "Medication Review Name"
    : "Minor Ailment Name";

export function FollowUpAppointments({
  education,
  formType,
  clinicalServiceName,
  serviceId,
}) {
  const [isSuppressed] = useFollowUpSuppression(serviceId);
  const followUpLabel = getFollowUpRowLabel(clinicalServiceName);
  const followUpValue = isSuppressed ? "No Follow Up" : getFollowUpValue(education);
  const topicsValue = isSuppressed ? "" : getTopicsValue(education, clinicalServiceName);
  const otherRowLabel = getOtherRowLabel(clinicalServiceName);
  const hasVaccines = !isSuppressed && (education?.vaccineSchedule?.length ?? 0) > 0;

  return (
    <Section formType={formType}>
      <H2>Follow Up Appointments</H2>
      <div className="space-y-1">
        <div className="flex flex-row gap-x-6 whitespace-nowrap">
          <DataField label={followUpLabel} value={followUpValue} />
          {/* {!isAllergicRhinitis(clinicalServiceName) && (
            <DataField label="Topics" value={topicsValue ?? ""} />
          )} */}
        </div>
        <DataField label={otherRowLabel} value="" />
        <DataField label="Smoking Cessation" />
        {hasVaccines ? (
          <table className="text-xs border border-gray-400 border-collapse w-full rounded-md">
            <thead>
              <tr className="text-left bg-gray-20">
                <th className="border font-normal border-gray-400 p-1">
                  Name of Vaccine
                </th>
                <th className="border font-normal border-gray-400 p-1">Date</th>
                <th className="border font-normal border-gray-400 p-1">Time</th>
              </tr>
            </thead>
            <tbody>
              {education.vaccineSchedule.map((entry, idx) => {
                const date = entry?.bookingDate ? formatDateToDD_MMM_YYYY(entry.bookingDate) : "Not scheduled";
                const time = entry?.bookingDate ? formatTimeFromISO(entry.bookingDate) : "";
                return (
                  <tr className="font-semibold" key={`${entry.name}-${idx}`}>
                    <td className="border border-gray-400 p-1">
                      {capitalizeFirstLetter(entry.name)}
                    </td>
                    <td className="border border-gray-400 p-1">{date}</td>
                    <td className="border border-gray-400 p-1">
                      {time || "Not scheduled"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <DataField label="Vaccine Health" value="No Vaccine Appointment" />
        )}
      </div>
    </Section>
  );
}
